import { useCallback, useEffect } from "react";
import CreateCharacterMpProvider from "@/core/data-provider/data-mp/create-character/create-character-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import useSetLayer from "@/core/helpers/useSetLayer";
import { useKeyPress } from "@/core/keyPressManager";
import useInterfaceSoundCore from "@/shared/hooks/useInterfaceSoundCore";
import { Soundtrack } from "@/shared/sounds";
import { Layer } from "@/domains/createCharacter/constants/layer";
import { setStep, stepSelector, stepValuesSelector } from "@/domains/createCharacter/slice";
import useReset from "@/domains/createCharacter/hooks/useReset";
import useShowCreateCharacter from "@/domains/createCharacter/hooks/useShowCreateCharacter";
import useValidationPersonalData from "@/domains/createCharacter/hooks/useValidationPersonalData";
import CreateCharacterBackBtnContainer from "@/domains/createCharacter/containers/CreateCharacterBackBtnContainer";
import CreateCharacterCameraContainer from "@/domains/createCharacter/containers/CreateCharacterCameraContainer";
import CreateCharacterRandomAllContainer from "@/domains/createCharacter/containers/CreateCharacterRandomAllContainer";
import CreateCharacterStartGameContainer from "@/domains/createCharacter/containers/CreateCharacterStartGameContainer";
import CreateCharacterUserInfoContainer from "@/domains/createCharacter/containers/CreateCharacterUserInfoContainer";
import CreateCharacterClothesContainer from "@/domains/createCharacter/containers/appearance/CreateCharacterClothesContainer";
import CreateCharacterDataContainer from "@/domains/createCharacter/containers/appearance/CreateCharacterDataContainer";
import CreateCharacterFaceContainer from "@/domains/createCharacter/containers/appearance/CreateCharacterFaceContainer";
import CreateCharacterGeneticsContainer from "@/domains/createCharacter/containers/appearance/CreateCharacterGeneticsContainer";
import CreateCharacterHairStyleContainer from "@/domains/createCharacter/containers/appearance/CreateCharacterHairStyleContainer";
import CreateCharacterHeadOverlayContainer from "@/domains/createCharacter/containers/appearance/CreateCharacterHeadOverlayContainer";
import CreateCharacter from "@/domains/createCharacter/components/CreateCharacter";
import CreateCharacterHotkeys from "@/domains/createCharacter/components/CreateCharacterHotkeys";
import CreateCharacterSteps from "@/domains/createCharacter/components/CreateCharacterSteps";
import KeyCode from "@/assets/constants/key-code";

export default function CreateCharacterContainer() {
  const { isShow } = useShowCreateCharacter();
  const { play, pause, isPlaying, stop } = useInterfaceSoundCore(Soundtrack.AccountBackground);
  useReset();
  useValidationPersonalData();
  const { currLayer } = useSetLayer(Layer);
  const dispatch = useAppDispatch();
  const { subscribeKeyDown, unsubscribeKeyDown } = useKeyPress();
  const stepValues = useAppSelector(stepValuesSelector);
  const step = useAppSelector(stepSelector);

  useEffect(() => {
    play();

    return () => {
      stop();
    };
  }, []);

  const toggleAudioHandler = useCallback(
    (key: KeyCode) => {
      if (currLayer !== Layer) return;
      switch (key) {
        case KeyCode.M: {
          const el = document.activeElement as HTMLElement | null;
          const tag = el?.tagName.toLowerCase();

          const isInputFocused = tag === "input" || tag === "textarea" || el?.isContentEditable;

          if (!isInputFocused) {
            if (isPlaying) {
              pause();
            } else {
              play();
            }
            // toggleAudio();
          }
          break;
        }
        default:
          break;
      }
    },
    [isPlaying, play, pause, currLayer],
  );

  useEffect(() => {
    const key = "toogle-audio-handler";
    subscribeKeyDown(key, toggleAudioHandler);

    return () => {
      unsubscribeKeyDown(key);
    };
  }, [toggleAudioHandler, subscribeKeyDown, unsubscribeKeyDown]);

  const onChangeStep = useCallback(
    (value: number) => {
      dispatch(setStep(value));
    },
    [dispatch],
  );

  if (!isShow) return null;

  return (
    <>
      <CreateCharacterSteps steps={stepValues} step={step} setStep={onChangeStep} />
      <CreateCharacterCameraContainer />
      <CreateCharacterBackBtnContainer />
      <CreateCharacterRandomAllContainer />
      <CreateCharacterUserInfoContainer />
      <div className="absolute bottom-[--boundary-offset-x] left-[--boundary-offset-x] flex items-end gap-6">
        <CreateCharacterHotkeys />
        <CreateCharacterStartGameContainer />
      </div>
      <CreateCharacter onHover={CreateCharacterMpProvider.setBrowserCefState}>
        {step === 0 && <CreateCharacterDataContainer />}
        {step === 1 && <CreateCharacterGeneticsContainer />}
        {step === 2 && <CreateCharacterFaceContainer />}
        {step === 3 && <CreateCharacterHairStyleContainer />}
        {step === 4 && <CreateCharacterHeadOverlayContainer />}
        {step === 5 && <CreateCharacterClothesContainer />}
      </CreateCharacter>
    </>
  );
}
